/* AthleteQuickStatsGrid — iter454
 * ─────────────────────────────────────────────────────────────────────
 * Tap-to-edit grid of 6 quick stats shown right below the profile hero:
 *   HEIGHT · WEIGHT · 40-YD · GPA · SAT · ACT
 *
 * Viewer mode:  only tiles with a non-empty value render (empty stats
 *               stay hidden so the grid never shows "—" placeholders).
 * Owner mode:   every tile renders. Empty tiles show a subtle "+ Add"
 *               state. Tapping any tile flips it into an inline <input>
 *               that saves on blur / Enter via PUT /me/athlete/profile.
 *
 * Data mapping:
 *   height / weight / forty_yard_dash → top-level fields on the athlete
 *   gpa / sat / act                    → nested inside athletes.academics
 * The server merges `academics` as a dict — we always PUT the merged
 * dict so a change to `gpa` never clobbers `sat` / `act`.
 *
 * All API-writing lives here — the parent just passes `athlete` +
 * `isOwner`, plus an optional `onSaved(patch)` to refresh the cached
 * athlete doc in place. No page reload required.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';
import { haptic } from '../../lib/nativeBridge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Field descriptors — order matters (this is the render order).
// Height uses a scroll picker (4'0" – 8'0" in 1-inch steps) so athletes
// on iOS get a native wheel instead of typing a string that could drift
// in format ("6-6", "66", `6'6`, `6'6"`). Everything else is a text
// input; the `inputMode` hint forces iOS to open the numeric keypad
// where the value is strictly digits.
const HEIGHT_OPTIONS = (() => {
  const out = [];
  for (let ft = 4; ft <= 8; ft += 1) {
    const maxIn = ft === 8 ? 0 : 11;
    for (let inch = 0; inch <= maxIn; inch += 1) {
      out.push(`${ft}'${inch}"`);
    }
  }
  return out;
})();

const STATS = [
  { key: 'height',           label: 'HEIGHT',      placeholder: `6'2"`,     scope: 'top',      inputMode: 'text',    max: 8, type: 'select', options: HEIGHT_OPTIONS },
  { key: 'weight',           label: 'WEIGHT',      placeholder: '195',      scope: 'top',      inputMode: 'numeric', max: 4   },
  { key: 'forty_yard_dash',  label: '40 YD',       placeholder: '4.85',     scope: 'top',      inputMode: 'decimal', max: 6   },
  { key: 'gpa',              label: 'GPA',         placeholder: '3.8',      scope: 'academics',inputMode: 'decimal', max: 5   },
  { key: 'sat',              label: 'SAT',         placeholder: '1430',     scope: 'academics',inputMode: 'numeric', max: 4   },
  { key: 'act',              label: 'ACT',         placeholder: '31',       scope: 'academics',inputMode: 'numeric', max: 2   },
];

const readValue = (athlete, stat) => {
  if (!athlete) return '';
  if (stat.scope === 'academics') {
    const v = (athlete.academics || {})[stat.key];
    return v == null || v === '' ? '' : String(v);
  }
  const v = athlete[stat.key];
  return v == null || v === '' ? '' : String(v);
};

const AthleteQuickStatsGrid = ({ athlete, isOwner, onSaved }) => {
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingKey && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editingKey]);

  const values = useMemo(
    () => Object.fromEntries(STATS.map((s) => [s.key, readValue(athlete, s)])),
    [athlete],
  );

  // Which tiles to render? Viewers see only populated tiles. Owners see all.
  const visibleStats = isOwner ? STATS : STATS.filter((s) => (values[s.key] || '').trim());

  // Empty grid for viewers → render nothing (keeps the profile clean
  // when the athlete hasn't filled in any of the 6 fields yet).
  if (!visibleStats.length) return null;

  const beginEdit = (stat) => {
    if (!isOwner || saving) return;
    setEditingKey(stat.key);
    setDraft(values[stat.key] || '');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft('');
  };

  const commitEdit = async (stat) => {
    if (!isOwner) return cancelEdit();
    const clean = (draft || '').trim();
    // No-op if the value didn't actually change — avoids toast spam.
    if (clean === (values[stat.key] || '')) return cancelEdit();
    setSaving(true);
    try {
      let payload;
      if (stat.scope === 'academics') {
        // Merge — never clobber the other academic fields.
        const merged = { ...(athlete?.academics || {}) };
        if (clean) merged[stat.key] = clean;
        else delete merged[stat.key];
        payload = { academics: merged };
      } else {
        payload = { [stat.key]: clean || null };
      }
      const r = await axios.put(
        `${API}/me/athlete/profile`,
        { updates: payload },
        { withCredentials: true },
      );
      // Hand back only the fields we touched so parent state stays lean.
      const patch = stat.scope === 'academics'
        ? { academics: r.data?.academics ?? payload.academics }
        : { [stat.key]: r.data?.[stat.key] ?? clean };
      onSaved?.(patch);
      // iter481 · Confident "saved" haptic on primary CTA.
      haptic('medium');
      toast.success(`${stat.label} saved — live on the network`);
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Save-on-select for wheel-picker stats (height). Skips the inline
  // text-edit flow entirely — the native <select> is the whole
  // interaction. Idempotent when the pick equals the current value.
  const commitSelect = async (stat, next) => {
    if (!isOwner) return;
    const clean = (next || '').trim();
    if (clean === (values[stat.key] || '')) return;
    setSaving(true);
    setEditingKey(stat.key);
    try {
      let payload;
      if (stat.scope === 'academics') {
        const merged = { ...(athlete?.academics || {}) };
        if (clean) merged[stat.key] = clean;
        else delete merged[stat.key];
        payload = { academics: merged };
      } else {
        payload = { [stat.key]: clean || null };
      }
      const r = await axios.put(
        `${API}/me/athlete/profile`,
        { updates: payload },
        { withCredentials: true },
      );
      const patch = stat.scope === 'academics'
        ? { academics: r.data?.academics ?? payload.academics }
        : { [stat.key]: r.data?.[stat.key] ?? clean };
      onSaved?.(patch);
      toast.success(`${stat.label} saved — live on the network`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
      setEditingKey(null);
    }
  };

  return (
    <div
      className="grid grid-cols-6 gap-1 sm:gap-1.5 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 backdrop-blur-md p-1.5 sm:p-2"
      data-testid="athlete-quick-stats-grid"
    >
      {visibleStats.map((stat) => {
        const val = values[stat.key];
        const isEditing = editingKey === stat.key;
        const empty = !val;
        // iter454.2 · single-row 6-tile strip. Tiles are narrow (min-w-0
        // + text truncation) so 6 fit comfortably on a 390-430px screen.
        // iter474.b · `flex flex-col items-center justify-center` is
        // set on the shared class so the Height tile (a <div> wrapping
        // a native <select>) and the other tiles (<button> elements)
        // lay out their value / label at identical baselines. Without
        // this, browser-default button alignment nudges values up a
        // couple pixels and the row looks uneven.
        const cls = `relative rounded-xl px-1 py-2 text-center transition min-w-0 flex flex-col items-center justify-center ${
          isEditing
            ? 'bg-white/[0.10] ring-1 ring-blue-400/50'
            : empty
              ? 'bg-transparent hover:bg-white/[0.04]'
              : 'bg-transparent hover:bg-white/[0.06]'
        } ${isOwner && !isEditing ? 'cursor-pointer' : 'cursor-default'}`;

        // Wheel-picker branch — Height renders as a static tile with an
        // invisible <select> overlaid on top. Tapping the tile pops the
        // native iOS wheel picker directly (no inline text-edit state).
        if (isOwner && stat.type === 'select') {
          return (
            <div
              key={stat.key}
              className={cls}
              data-testid={`quick-stat-${stat.key}`}
            >
              {empty ? (
                <span className="inline-flex flex-col items-center justify-center gap-0.5 leading-none py-1">
                  <Plus className="w-3 h-3 text-white/40" strokeWidth={2.5} />
                </span>
              ) : (
                <p className="font-display text-sm sm:text-base text-white leading-none truncate px-0.5">
                  {val}
                </p>
              )}
              <p className={`text-[8px] sm:text-[9px] uppercase tracking-widest font-bold mt-1 truncate ${
                empty ? 'text-white/40' : 'text-white/60'
              }`}>
                {stat.label}
              </p>
              {/* Native <select> — invisible but stretched across the
                  tile so a tap opens the iOS wheel picker directly.
                  The visible tile above shows the current value. */}
              <select
                value={val || ''}
                onChange={(e) => { haptic('selection'); commitSelect(stat, e.target.value); }}
                disabled={saving}
                aria-label={`${stat.label} — choose a value`}
                data-testid={`quick-stat-select-${stat.key}`}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                style={{ fontSize: 16 }}
              >
                <option value="">— Select {stat.label.toLowerCase()} —</option>
                {stat.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {saving && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl pointer-events-none">
                  <Loader2 className="w-3 h-3 text-white animate-spin" />
                </span>
              )}
            </div>
          );
        }

        if (isEditing) {
          return (
            <div key={stat.key} className={cls} data-testid={`quick-stat-${stat.key}`}>
              <input
                ref={inputRef}
                type="text"
                inputMode={stat.inputMode}
                maxLength={stat.max}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => commitEdit(stat)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitEdit(stat); }
                  if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                }}
                placeholder={stat.placeholder}
                className="w-full bg-transparent font-display text-sm sm:text-base text-white text-center leading-none focus:outline-none"
                style={{ caretColor: '#60a5fa' }}
                data-testid={`quick-stat-input-${stat.key}`}
              />
              <p className="text-[8px] sm:text-[9px] uppercase tracking-widest font-bold mt-1 text-blue-300/80 truncate">
                {stat.label}
              </p>
              {saving && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl pointer-events-none">
                  <Loader2 className="w-3 h-3 text-white animate-spin" />
                </span>
              )}
            </div>
          );
        }

        return (
          <button
            key={stat.key}
            type="button"
            onClick={() => beginEdit(stat)}
            disabled={!isOwner || saving}
            className={cls}
            data-testid={`quick-stat-${stat.key}`}
            aria-label={`${stat.label}: ${val || 'not set'}${isOwner ? ' — tap to edit' : ''}`}
          >
            {empty ? (
              <span className="inline-flex flex-col items-center justify-center gap-0.5 leading-none py-1">
                <Plus className="w-3 h-3 text-white/40" strokeWidth={2.5} />
              </span>
            ) : (
              <p className="font-display text-sm sm:text-base text-white leading-none truncate px-0.5">
                {val}
              </p>
            )}
            <p className={`text-[8px] sm:text-[9px] uppercase tracking-widest font-bold mt-1 truncate ${
              empty ? 'text-white/40' : 'text-white/60'
            }`}>
              {stat.label}
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default AthleteQuickStatsGrid;
